// src/Components/BarcodeInput.jsx
import { Component } from 'react'
import styled from 'styled-components'
import { FaCheck, FaTimes, FaSpinner, FaBarcode } from 'react-icons/fa'
import { Api } from '../Services'
import ContentStyle from './ContentStyle'

// Styled Components
const BarcodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
  background: transparent;
  position: relative;
`

const BarcodeHeader = styled.div`
  display: ${({ compact }) => compact ? 'none' : 'flex'};
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

const BarcodeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${props => props.theme.colors.semantic.secondary};
  border-radius: ${props => props.theme.radius.button};
  color: ${props => props.theme.colors.text.inverse};
  font-size: 12px;
  transition: background 0.3s ease;
`

const BarcodeTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.secondary};
  transition: color 0.3s ease;
`

const BarcodeSubtitle = styled.div`
  font-size: 12px;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.text.muted};
  margin-top: 1px;
  transition: color 0.3s ease;
`

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const BarcodeInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const BarcodeInput = styled(ContentStyle.Text)`
  flex: 1;
  padding-right: ${({ hasIcon }) => hasIcon ? '40px' : '12px'};
  border-color: ${({ status, theme }) => {
    switch (status) {
      case 'valid': return theme.colors.semantic.success
      case 'invalid': return theme.colors.semantic.danger
      case 'checking': return theme.colors.semantic.warning
      default: return theme.colors.border.medium
    }
  }};

  &:focus {
    border-color: ${({ status, theme }) => {
      switch (status) {
        case 'valid': return theme.colors.semantic.success
        case 'invalid': return theme.colors.semantic.danger
        case 'checking': return theme.colors.semantic.warning
        default: return theme.colors.border.focus
      }
    }};
    box-shadow: 0 0 0 4px ${({ status, theme }) => {
      switch (status) {
        case 'valid': return theme.colors.semantic.successHover
        case 'invalid': return 'rgba(239, 68, 68, 0.1)'
        case 'checking': return 'rgba(245, 158, 11, 0.1)'
        default: return theme.colors.shadow.focus
      }
    }};
  }
`

const StatusIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ status, theme }) => {
    switch (status) {
      case 'valid': return theme.colors.semantic.success
      case 'invalid': return theme.colors.semantic.danger
      case 'checking': return theme.colors.semantic.warning
      default: return theme.colors.text.muted
    }
  }};
  font-size: 14px;
  pointer-events: none;
  transition: color 0.3s ease;

  ${({ status }) => status === 'checking' && `
    animation: spin 1s linear infinite;
  `}

  @keyframes spin {
    from { transform: translateY(-50%) rotate(0deg); }
    to { transform: translateY(-50%) rotate(360deg); }
  }
`

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-start;
`

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 12px;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${({ status, theme }) => {
    switch (status) {
      case 'valid': return theme.colors.semantic.success
      case 'invalid': return theme.colors.semantic.danger
      case 'checking': return theme.colors.semantic.warning
      default: return theme.colors.text.tertiary
    }
  }};
  font-weight: 400;
  min-height: 18px;
  transition: color 0.3s ease;

  svg {
    font-size: 12px;
  }
`

export default class BarcodeInputComponent extends Component {
  constructor (props) {
    super(props)
    this.state = {
      checking: false,
      status: 'default', // default, checking, valid, invalid
      message: ''
    }
    this.debounceTimer = null
  }

  componentDidMount () {
    const { value } = this.props
    if (value && value.length === 13) {
      this.verifyBarcode(value)
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.value !== this.props.value) {
      const { value } = this.props
      if (value && value.length === 13) {
        this.debouncedVerify(value)
      } else if (value && value.length > 0) {
        this.setState({ status: 'default', message: `${value.length}/13 cifre inserite` })
      } else {
        this.setState({ status: 'default', message: '' })
      }
    }
  }

  debouncedVerify = (barcode) => {
    clearTimeout(this.debounceTimer)
    this.setState({ status: 'checking', message: 'Verifica in corso...' })

    this.debounceTimer = setTimeout(() => {
      this.verifyBarcode(barcode)
    }, 800)
  }

  verifyBarcode = async (barcode) => {
    if (!barcode || barcode.length !== 13) {
      this.setState({ status: 'default', message: '' })
      return
    }

    this.setState({ checking: true, status: 'checking' })

    try {
      const response = await Api.post('/articoli/verify-barcode', {
        barcode,
        excludeId: this.props.excludeId || null
      })

      if (response.ok) {
        const { valid, available, error, existingArticle } = response.data

        if (valid && available) {
          this.setState({
            status: 'valid',
            message: '✓ Barcode valido e disponibile',
            checking: false
          })
          this.props.onValidationChange?.(true, null)
        } else {
          this.setState({
            status: 'invalid',
            message: error || 'Barcode non valido',
            checking: false
          })
          this.props.onValidationChange?.(false, error, existingArticle)
        }
      } else {
        this.setState({
          status: 'invalid',
          message: 'Errore durante la verifica',
          checking: false
        })
        this.props.onValidationChange?.(false, 'Errore durante la verifica')
      }
    } catch (error) {
      this.setState({
        status: 'invalid',
        message: 'Errore di connessione',
        checking: false
      })
      this.props.onValidationChange?.(false, 'Errore di connessione')
    }
  }

  generateBarcode = () => {
    // Rimuove il barcode per permettere la generazione automatica dal backend
    this.props.onChange('')
    this.setState({
      status: 'default',
      message: 'Barcode rimosso - sarà generato automaticamente al salvataggio'
    })
  }

  getStatusIcon = () => {
    const { status } = this.state
    switch (status) {
      case 'valid':
        return <FaCheck />
      case 'invalid':
        return <FaTimes />
      case 'checking':
        return <FaSpinner />
      default:
        return null
    }
  }

  render () {
    const {
      value,
      onChange,
      placeholder = 'Inserisci 13 cifre numeriche',
      disabled = false,
      compact = false
    } = this.props

    const { status, message, checking } = this.state
    const hasIcon = status !== 'default'

    return (
      <BarcodeContainer compact={compact}>
        {!compact && (
          <BarcodeHeader>
            <BarcodeIcon>
              <FaBarcode />
            </BarcodeIcon>
            <div>
              <BarcodeTitle>Barcode EAN-13</BarcodeTitle>
              <BarcodeSubtitle>
                Inserisci un codice personalizzato o genera automaticamente
              </BarcodeSubtitle>
            </div>
          </BarcodeHeader>
        )}

        <InputSection>
          <BarcodeInputWrapper>
            <BarcodeInput
              type='text'
              value={value || ''}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, '').substring(0, 13)
                onChange(numericValue)
              }}
              placeholder={placeholder}
              disabled={disabled || checking}
              status={status}
              hasIcon={hasIcon}
              maxLength={13}
              compact={compact}
            />

            {hasIcon && (
              <StatusIcon status={status}>
                {this.getStatusIcon()}
              </StatusIcon>
            )}
          </BarcodeInputWrapper>

          <ActionsRow>
            <StatusMessage status={status}>
              {message || (value ? (value.length < 13 ? `${value.length}/13 cifre` : '') : 'Se vuoi generare un barcode automaticamente, lascia vuoto')}
            </StatusMessage>
          </ActionsRow>
        </InputSection>
      </BarcodeContainer>
    )
  }
}
